import {useContext, useEffect} from 'react';
import {useRouter} from 'next/router';

import {UserStateContext} from '#component/provider';

import {UseUserStateParams} from './useUserState.types';

/**
 * The `useUserState` hook returns a boolean indicating if the user meets one
 * of a subset of possible authentication states:
 * - 'anonymous', meaning the user is not logged into the application.
 * - 'authenticated', meaning the user has logged into the application.
 * - 'verified', meaning the user has logged into the application, and has verified their account.
 *
 * By default, the hook will redirect the user if they do not meet the required
 * state. Default paths are set per authentication state, but the redirect behaviour,
 * and the path itself, may be configured via the `redirect` and `path` parameters.
 */
export const useUserState = ({state = 'verified', redirect = true, path}: UseUserStateParams) => {
  const {isAnonymous, isAuthenticated, isLoading, isVerified} = useContext(UserStateContext);
  const {replace, asPath} = useRouter();
  const encodedAsPath = asPath ? asPath.replace(/[^/]/g, match => encodeURIComponent(match)) : '';

  // This effect only concerns with "redirection related to screen state
  // requirement violation". For user action related redirections, use/modify
  // the `useAuth` hook instead.
  useEffect(() => {
    if (redirect && !isLoading) {
      if (state === 'anonymous' && isAuthenticated) {
        replace(path || '/dashboard');
      } else if (state === 'authenticated' && !isAuthenticated) {
        replace(path || `/signin${encodedAsPath ? `?return_to=${encodedAsPath}` : ''}`);
      } else if (state === 'verified' && !isVerified) {
        replace(
          path || isAuthenticated
            ? '/verifyaccount'
            : `/signin${encodedAsPath ? `?return_to=${encodedAsPath}` : ''}`,
        );
      }
    }
  }, [isAuthenticated, isVerified, isLoading, path, encodedAsPath, redirect, replace, state]);

  if (state === 'anonymous') {
    return isAnonymous;
  } else if (state === 'authenticated') {
    return isAuthenticated;
  } else if (state === 'verified') {
    return isVerified;
  }
};
